import {
  ArrowBack,
  DownloadRounded,
  LockOpenRounded,
  LockRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { fetchBatchCsv } from "@/api/accounting";
import { periodLabel } from "@/components/accounting/PeriodsListPanel";
import {
  useBatches,
  useClosePeriod,
  useCreateBatch,
  useExportPreview,
  usePeriodEvents,
  usePeriods,
  useReopenPeriod,
} from "@/hooks/useAccounting";
import { useInvoicePeriodCheck } from "@/hooks/useInvoiceSync";
import { useUnpostedReleases } from "@/hooks/useSubscriptionSchedules";
import { PERIOD_STATUS } from "@/interfaces/accounting";
import { formatDate, formatDateTime } from "@/utils/date-format";

const money = (v: string | null | undefined) =>
  Number(v ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "var(--color-graphite)",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, fontWeight: 500 }}>{children}</Box>
    </Box>
  );
}

const card = {
  border: "1px solid var(--color-card-border)",
  borderRadius: "var(--radius-card)",
  p: { xs: 2, md: 3 },
  mb: 3,
} as const;

const th = {
  fontSize: 11,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 600,
  borderBottom: "1px solid var(--color-card-border)",
  whiteSpace: "nowrap" as const,
};

/** Downloads a string as a file. */
function download(filename: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * One accounting period — its summary, then its posting batches.
 *
 * This is where the close cycle is actually run: review what would go out, export a batch, confirm
 * QuickBooks accepted it, close the month.
 */
export default function PeriodDetail({ period, onBack }: { period: string; onBack: () => void }) {
  const { data: periods = [] } = usePeriods();
  const current = periods.find((p) => p.period === period);
  const closed = current?.status === PERIOD_STATUS.closed;

  const { data: preview, isLoading } = useExportPreview(period);
  const { data: batches = [] } = useBatches(period);
  const { data: events = [] } = usePeriodEvents(period);
  const createBatch = useCreateBatch(period);
  const closeP = useClosePeriod(period);
  const reopenP = useReopenPeriod(period);

  // Subscription revenue for this month or earlier that should have been released and was not —
  // the server refuses to close the month until recognition is rerun, so say so before Close.
  const { data: unposted } = useUnpostedReleases(closed ? null : period.slice(0, 7));
  const unpostedCount = unposted?.count ?? 0;

  // Things in Stripe dated this month or earlier that aren't in v2's books (an invoice, a payment, a
  // card purchase, a payout) — the server refuses to close until they are synced. Null data = the
  // check does not run here.
  const invoiceCheck = useInvoicePeriodCheck(closed ? null : period.slice(0, 7));
  const invoicesToFix = invoiceCheck.data?.toFix ?? 0;
  const invoicesReported = invoiceCheck.data?.reported ?? 0;
  const blockers = invoiceCheck.data?.blockers ?? [];

  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const held = preview?.heldForReview ?? [];
  // A period can't be closed while entries in it have never been put in a batch — that would be
  // declaring a month final while part of it has never reached QuickBooks.
  const pendingCount = preview?.exportableCount ?? 0;
  // Months are worked in sequence, so an earlier open period blocks this one entirely.
  const blockedBy = preview?.blockedByPeriod ?? null;
  const isLatest = preview ? !preview.hasLaterPeriod : false;

  const postBlockedReason = blockedBy
    ? `${periodLabel(blockedBy)} is still open — finish it first`
    : pendingCount === 0
      ? "Nothing new to post"
      : "";
  const closeBlockedReason = blockedBy
    ? `${periodLabel(blockedBy)} is still open — close it first`
    : isLatest
      ? "This is the latest period — closing it would leave none open"
      : unpostedCount > 0
        ? `${unpostedCount} subscription revenue ${unpostedCount === 1 ? "release has" : "releases have"} not posted — rerun recognition first`
        : invoiceCheck.isError
          ? "Stripe could not be reached to check this month's invoices"
          : invoicesToFix > 0
            ? `${invoicesToFix} ${invoicesToFix === 1 ? "thing in Stripe isn't" : "things in Stripe aren't"} in v2's books — run Sync with Stripe first`
            : pendingCount > 0
              ? `${pendingCount} ${pendingCount === 1 ? "entry has" : "entries have"} not been posted to a batch`
              : "";

  /** Runs an action and reports what it did. The message comes from the action so it can name the
   *  batch it just created. */
  const run = async (fn: () => Promise<string>) => {
    setError(null);
    try {
      setNote(await fn());
    } catch (e) {
      setError(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (e instanceof Error ? e.message : "Something went wrong."),
      );
    }
  };

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{ textTransform: "none", color: "var(--color-graphite)", mb: 2 }}
      >
        Periods
      </Button>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{periodLabel(period)}</Typography>
        <Chip
          size="small"
          icon={closed ? <LockRounded fontSize="small" /> : <LockOpenRounded fontSize="small" />}
          label={closed ? "Closed" : "Open"}
          color={closed ? "default" : "success"}
          variant={closed ? "filled" : "outlined"}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary. */}
      <Box sx={card}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 3,
          }}
        >
          <Fact label="Period">
            {current ? `${formatDate(current.periodFrom)} – ${formatDate(current.periodTo)}` : "—"}
          </Fact>
          <Fact label="Entries">{current?.entryCount ?? 0}</Fact>
          <Fact label="Debits">{money(current?.totalDebit)}</Fact>
          <Fact label="Credits">{money(current?.totalCredit)}</Fact>
          <Fact label="Signed off">
            {current?.closedAt ? formatDate(current.closedAt) : "Not yet"}
          </Fact>
          <Fact label="Signed by">{current?.closedByName ?? "—"}</Fact>
        </Box>
      </Box>

      {/* Pending to post — everything about what has NOT yet gone out, and the action that drafts
          it. The button belongs here rather than over the batch table: it acts on this set. */}
      <Box sx={card}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>Pending to post</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 3,
            mb: 2.5,
          }}
        >
          <Fact label="Entries ready">{preview?.exportableCount ?? 0}</Fact>
          <Fact label="Debits">{money(preview?.totalDebit)}</Fact>
          <Fact label="Credits">{money(preview?.totalCredit)}</Fact>
          <Fact label="Held for review">
            <Box component="span" sx={{ color: held.length > 0 ? "#b3261e" : undefined }}>
              {held.length}
            </Box>
          </Fact>
        </Box>

        {/* Held entries are EXCLUDED from the file, so this sits above the export button. */}
        {held.length > 0 && (
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              {held.length} {held.length === 1 ? "entry" : "entries"} will NOT be included
            </Typography>
            <Typography sx={{ fontSize: 12.5, mb: 1 }}>
              Their QuickBooks account couldn't be matched. Resolve them under Needs Review, or this
              period exports short.
            </Typography>
            {held.map((h) => (
              <Typography key={h.journalNo} sx={{ fontSize: 12, fontFamily: "monospace" }}>
                {h.journalNo} · {formatDate(h.accountingDate)} · {h.reasonCode}
              </Typography>
            ))}
          </Alert>
        )}

        {unpostedCount > 0 && (
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              Subscription revenue for this month has not been released
            </Typography>
            <Typography sx={{ fontSize: 12.5 }}>
              {unpostedCount} {unpostedCount === 1 ? "release" : "releases"} (
              {money(unposted?.amount)})
              {unposted?.earliestPeriod
                ? ` from ${periodLabel(`${unposted.earliestPeriod}-01`)} onwards`
                : ""}{" "}
              should have posted and have not — the recognition run failed or did not run. The month
              can't be closed until they post. Rerun recognition from Accounting › Deferred
              Subscriptions.
            </Typography>
          </Alert>
        )}

        {(invoicesToFix > 0 || invoiceCheck.isError) && (
          <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              {invoiceCheck.isError
                ? "This month's invoices could not be checked against Stripe"
                : `${periodLabel(period)} can't be closed yet: ${invoicesToFix} ${invoicesToFix === 1 ? "thing in Stripe isn't" : "things in Stripe aren't"} in v2's books`}
            </Typography>
            {invoiceCheck.isError ? (
              <Typography sx={{ fontSize: 12.5 }}>
                Stripe could not be reached. The month can't be closed until the check runs.
              </Typography>
            ) : (
              <>
                <Box component="ul" sx={{ m: 0, mb: 0.75, pl: 2.5, listStyleType: "disc" }}>
                  {blockers.map((b) => (
                    <Box component="li" key={b.text} sx={{ fontSize: 12.5, mb: 0.25 }}>
                      {b.text}
                      <Box component="span" sx={{ color: "var(--color-graphite)" }}>
                        {" "}
                        — {b.examples.join(", ")}
                        {b.more > 0 ? ` and ${b.more} more` : ""}
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 12.5 }}>
                  Run Sync with Stripe from Finance › Invoices to fix them.
                </Typography>
              </>
            )}
          </Alert>
        )}

        {invoicesReported > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 12.5 }}>
              {invoicesReported} {invoicesReported === 1 ? "item" : "items"} in Stripe{" "}
              {invoicesReported === 1 ? "is" : "are"} left for a person or waiting to be imported.
              They don't block the close; Sync with Stripe lists them.
            </Typography>
          </Alert>
        )}

        {/* The summary that accompanies the file, so a period difference is explained up front. */}
        {(preview?.carriedIn.length ?? 0) > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              Carried in from an earlier period
            </Typography>
            {preview?.carriedIn.map((g) => (
              <Typography key={g.fromPeriod} sx={{ fontSize: 12.5 }}>
                {periodLabel(g.fromPeriod)} money recognised here — {g.count}{" "}
                {g.count === 1 ? "entry" : "entries"}, ${money(g.total)}
              </Typography>
            ))}
            <Typography sx={{ fontSize: 12, mt: 0.5, opacity: 0.85 }}>
              Send this with the file — it explains why the two months won't tie exactly.
            </Typography>
          </Alert>
        )}

        <Tooltip title={postBlockedReason}>
          <span>
            <Button
              variant="contained"
              startIcon={<DownloadRounded />}
              disabled={postBlockedReason !== "" || createBatch.isPending}
              onClick={() =>
                run(async () => {
                  const b = await createBatch.mutateAsync();
                  download(`gfg-journal-${b.period.slice(0, 7)}-batch-${b.batchId}.csv`, b.csv);
                  return `Batch #${b.batchId} posted — ${b.entryCount} entries downloaded for QuickBooks.`;
                })
              }
              sx={{
                textTransform: "none",
                bgcolor: "var(--color-rodeo-black)",
                "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
              }}
            >
              {createBatch.isPending ? "Posting…" : "Post Batch"}
            </Button>
          </span>
        </Tooltip>
        <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 1 }}>
          Posts every entry above into a batch and downloads the QuickBooks file. Entries already in
          a batch are never included again, so posting twice is safe.
        </Typography>
        {blockedBy && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {periodLabel(blockedBy)} is still open. Periods are worked in order, so it has to be
            closed before {periodLabel(period)} can be posted — otherwise two months are being
            reconciled at once and entries end up in the wrong one.
          </Alert>
        )}
      </Box>

      {/* Posting batches. */}
      <Box sx={card}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>Posting Batches</Typography>

        {isLoading ? (
          <CircularProgress aria-label="Loading" size={22} />
        ) : batches.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Nothing exported for this period yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={th}>Batch</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Entries</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Dr</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Cr</TableCell>
                  <TableCell sx={th}>Exported</TableCell>
                  <TableCell sx={th}>Status</TableCell>
                  <TableCell sx={{ ...th, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>#{b.id}</TableCell>
                    <TableCell sx={{ textAlign: "right", fontSize: 13 }}>{b.entryCount}</TableCell>
                    <TableCell
                      sx={{ textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                    >
                      {money(b.totalDebit)}
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                    >
                      {money(b.totalCredit)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {b.postedAt ? formatDateTime(b.postedAt) : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label="Posted" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {/* Re-downloadable from stored membership, so a lost file is a re-download
                          rather than a reason to post again. */}
                      <Button
                        size="small"
                        startIcon={<DownloadRounded />}
                        sx={{ textTransform: "none" }}
                        onClick={() =>
                          run(async () => {
                            download(`gfg-journal-batch-${b.id}.csv`, await fetchBatchCsv(b.id));
                            return "File downloaded.";
                          })
                        }
                      >
                        CSV
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Close / reopen. */}
      <Box sx={card}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1.5 }}>Period status</Typography>
        {closed ? (
          <>
            <Typography sx={{ fontSize: 13, mb: 1.5 }}>
              Closed. Entries dated into this period are recorded in the current open one instead,
              keeping their original date.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<LockOpenRounded />}
              sx={{ textTransform: "none" }}
              disabled={reopenP.isPending}
              onClick={() =>
                run(async () => {
                  await reopenP.mutateAsync();
                  return `${periodLabel(period)} reopened.`;
                })
              }
            >
              Reopen
            </Button>
            <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 1 }}>
              Reopening allows new entries to be dated here. It does not move entries already
              recorded in a later period — those may already have been exported.
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 13, mb: 1.5 }}>
              Close once the Financial Controller has confirmed the period is agreed.
            </Typography>
            {isLatest && !blockedBy && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This is the latest period, so closing it would leave none open and new entries would
                have nowhere to land. It can be closed once a later period exists — one is created
                automatically the first time anything posts into the following month.
              </Alert>
            )}
            {/* Disabled with the reason shown, rather than failing on click. */}
            <Tooltip title={closeBlockedReason}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<LockRounded />}
                  disabled={(preview?.exportableCount ?? 0) > 0 || closeP.isPending}
                  onClick={() =>
                    run(async () => {
                      await closeP.mutateAsync();
                      return `${periodLabel(period)} closed.`;
                    })
                  }
                  sx={{
                    textTransform: "none",
                    bgcolor: "var(--color-rodeo-black)",
                    "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
                  }}
                >
                  Close {periodLabel(period)}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Activity. Read from the append-only event log rather than the period row, which only
          remembers the LAST close and reopen — a month closed, reopened and closed again has a
          history the row cannot hold. */}
      <Box sx={card}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>Activity</Typography>
        {events.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            Nothing has happened to this period yet.
          </Typography>
        ) : (
          events.map((e) => (
            <Box
              key={e.id}
              sx={{
                display: "flex",
                gap: 2,
                py: 1.25,
                borderTop: "1px solid var(--color-card-border)",
                flexWrap: "wrap",
                alignItems: "baseline",
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)", minWidth: 150 }}>
                {formatDateTime(e.createdAt)}
              </Typography>
              <Typography sx={{ fontSize: 13, flex: 1, minWidth: 240 }}>
                {e.statusDescription}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
                {/* No actor means a webhook or a cron did it, which is worth saying explicitly. */}
                {e.actorName ?? "System"}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      <Snackbar
        open={note != null}
        autoHideDuration={4000}
        onClose={() => setNote(null)}
        message={note ?? ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
